import React, { useState, lazy, Suspense } from "react";
import { useAuth } from "../../context/AuthProvider";
import { WalletSection } from "./WalletSection";
import { DirectUpiAdminPanel } from "./DirectUpiAdminPanel";
import { KitchenSection } from "./KitchenSection";
import { ChefSection } from "./ChefSection";
import { UserSection } from "./UserSection";
import { OrderSection } from "./OrderSection";
import { DeliveryAgentSection } from "./DeliveryAgentSection";
import { RateCardSection } from "./RateCardSection";
import { Spinner } from "../../components/common/Spinner";
import AdminOrderListener from "../../components/admin/AdminOrderListener";
import { WhatsAppDashboard } from "../../components/admin/whatsapp/WhatsAppDashboard";
import { NoticeManager } from "../../components/admin/NoticeManager";
import { WhatsAppBroadcastPanel } from "../../components/admin/WhatsAppBroadcastPanel";
import { BlockedDatesSection } from "./BlockedDatesSection";
import { CommunitiesSection } from "./CommunitiesSection";

const DailyTiffinAdmin = lazy(() => import("./DailyTiffinAdmin"));

const sections = [
  { key: "notices", label: "Announcements" },
  { key: "whatsapp", label: "💬 WhatsApp" },
  { key: "wallet", label: "Wallet" },
  { key: "ratecard", label: "Rate Card" },
  { key: "blocked-dates", label: "📅 Blocked Dates" },
  { key: "tiffin", label: "Daily Tiffin" },
  { key: "communities", label: "🏘️ Communities" },  // ← RENAMED
  { key: "kitchens", label: "Kitchens" },
  { key: "chefs", label: "Chefs" },
  { key: "users", label: "Users" },
  { key: "orders", label: "Orders" },
  { key: "deliveryAgents", label: "Delivery Agents" },
];

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [refresh, setRefresh] = useState(0);
  const [selected, setSelected] = useState("wallet");

  const handleAdminEvent = () => setRefresh((r) => r + 1);

  if (!user || user.currentRole !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-center p-10 font-bold text-red-500">
          Access Denied
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-4 sm:py-8 px-3 sm:px-4 pb-24 sm:pb-8">
      <AdminOrderListener />
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold">Platform Administration</h2>
          <p className="text-gray-500 mt-1">
            Manage global settings, users, and kitchens.
          </p>
        </div>
        <div className="text-right">
          <span className="inline-block px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-bold uppercase tracking-wider">
            Super Admin
          </span>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-4 sm:mb-6 overflow-x-auto pb-2 -mx-3 px-3 sm:mx-0 sm:px-0 no-scrollbar sticky top-0 z-10 bg-gray-50/95 backdrop-blur py-2">
        {sections.map((sec) => (
          <button
            key={sec.key}
            className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
              selected === sec.key
                ? "bg-blue-600 text-white font-bold shadow-sm"
                : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
            onClick={() => setSelected(sec.key)}
          >
            {sec.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <Suspense
        fallback={
          <div className="flex justify-center p-10">
            <Spinner size={48} />
          </div>
        }
      >
        {selected === "whatsapp" ? (
          <div className="rounded-xl shadow-sm border border-gray-100 overflow-hidden h-[min(70vh,640px)] sm:h-[calc(100vh-220px)] min-h-[420px] flex flex-col">
            <WhatsAppDashboard />
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 min-h-[320px] sm:min-h-[500px] p-3 sm:p-4 overflow-x-auto">
            {selected === "notices" && (
              <>
                <NoticeManager />
                <WhatsAppBroadcastPanel />
              </>
            )}
            {selected === "wallet" && (
              <WalletSection trigger={refresh} onAdminEvent={handleAdminEvent} />
            )}
            {selected === "wallet" && (
              <DirectUpiAdminPanel />
            )}
            {selected === "ratecard" && <RateCardSection />}
            {selected === "blocked-dates" && (
              <BlockedDatesSection
                trigger={refresh}
                onAdminEvent={handleAdminEvent}
              />
            )}
            {selected === "tiffin" && <DailyTiffinAdmin />}
            {selected === "communities" && <CommunitiesSection trigger={refresh} onAdminEvent={handleAdminEvent} />}
            {selected === "kitchens" && (
              <KitchenSection
                trigger={refresh}
                onAdminEvent={handleAdminEvent}
              />
            )}
            {selected === "chefs" && (
              <ChefSection trigger={refresh} onAdminEvent={handleAdminEvent} />
            )}
            {selected === "users" && (
              <UserSection trigger={refresh} onAdminEvent={handleAdminEvent} />
            )}
            {selected === "orders" && (
              <OrderSection trigger={refresh} onAdminEvent={handleAdminEvent} />
            )}
            {selected === "deliveryAgents" && (
              <DeliveryAgentSection
                trigger={refresh}
                onAdminEvent={handleAdminEvent}
              />
            )}
          </div>
        )}
      </Suspense>
    </div>
  );
};

export default AdminDashboard;
